import type { HttpErrorResponse } from '@angular/common/http';

import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, Subscription } from 'rxjs';
import { concatMap, map, retry, tap } from 'rxjs/operators';
import {
  IGroupEnrollment,
  IGroupEnrollmentLog,
  IGroupEnrollmentPayer,
  IGroupEnrollmentStatus,
  type IGroupEnrollmentUpdate,
} from 'src/app/shared/interfaces/group-enrollment/group-enrollment.interface';
import { AlertService } from 'src/app/shared/services/alert.service';
import { GroupEnrollmentGQLService } from 'src/app/shared/services/group-enrollment/insurance/group-enrollment.graphql.service';
import { GroupEnrollmentService } from 'src/app/shared/services/group-enrollment/insurance/group-enrollment.service';
import { DEFAULT_MODAL_OPTIONS } from 'src/app/shared/variables/variables';

import { EditGroupEnrollmentNoteModalComponent } from '../edit-group-enrollment-note-modal/edit-group-enrollment-note-modal.component';

@Component({
  selector: 'app-view-modify-enrollments-modal',
  templateUrl: './view-modify-enrollments-modal.component.html',
  styleUrls: ['./view-modify-enrollments-modal.component.scss'],
})
export class ViewModifyEnrollmentsModalComponent implements OnInit, OnDestroy {
  seqNo: number;

  groupEnrollment: IGroupEnrollment;
  groupEnrollmentForm: FormGroup;

  documents: string[];
  selectedPayer?: IGroupEnrollmentPayer;
  statuses: IGroupEnrollmentStatus[];
  groupEnrollmentPayers: IGroupEnrollmentPayer[] = [];
  logs: IGroupEnrollmentLog[];

  npiList: string[] = [];
  taxIdList: string[] = [];

  isLoading = true;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private groupEnrollmentService: GroupEnrollmentService,
    private groupEnrollmentGQLService: GroupEnrollmentGQLService,
    private alertService: AlertService,
    private modal: NgbModal,
  ) {}

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.groupEnrollmentForm = this.fb.group({
      enrollmentStatus: [''],
      processorId: [{ value: '', disabled: false }, [Validators.maxLength(12)]],
      followUpDate: [null],
      message: ['', [Validators.maxLength(1500)]],
      actionNotes: ['', [Validators.maxLength(16_000)]],
      caseNumber: [{ value: '', disabled: false }, [Validators.maxLength(50)]],
    });

    const getGroupEnrollment$ = this.groupEnrollmentGQLService
      .getGroupEnrollment(this.seqNo)
      .pipe(
        tap((x) => {
          this.groupEnrollment = x;
          this.setFormValue(this.groupEnrollment);
        }),
      );

    this.subscriptions.add(
      getGroupEnrollment$
        .pipe(
          concatMap(() => {
            return combineLatest(this.getAdditionalRequestsList());
          }),
          retry(1),
        )
        .subscribe({
          next: ([
            groupEnrollmentStatuses,
            groupEnrollmentPayers,
            groupEnrollmentLogs,
          ]) => {
            const foundedPayers =
              groupEnrollmentPayers as IGroupEnrollmentPayer[];
            const groupEnrollmentPayer = foundedPayers.find((x) => {
              return x.enrollmentType === this.groupEnrollment.enrollmentType;
            });

            this.statuses = groupEnrollmentStatuses as IGroupEnrollmentStatus[];
            this.groupEnrollmentPayers =
              groupEnrollmentPayers as IGroupEnrollmentPayer[];
            this.selectedPayer = groupEnrollmentPayer;

            this.logs = (groupEnrollmentLogs as IGroupEnrollmentLog[]).map(
              (log) => {
                return {
                  ...log,
                  status:
                    this.statuses.find((x) => {
                      return log.status === x.code;
                    })?.description ?? log.status,
                };
              },
            );

            const payerInfo = this.selectedPayer
              ? `${this.selectedPayer.scId} - ${this.selectedPayer.payerName}`
              : `${this.groupEnrollment.payerId} - ${this.groupEnrollment.payer?.name}`;

            if (this.selectedPayer) {
              this.setPayer(this.selectedPayer);
            }

            this.groupEnrollmentForm.patchValue({
              payerId: payerInfo,
            });

            this.isLoading = false;
          },
          error: (error) => {
            this.isLoading = false;
            console.log(error);
            this.alertService.error('Something went wrong. Please try again.');
          },
        }),
    );
  }

  download(file: { item: string }) {
    try {
      const fileName = file.item;
      const checkFileType = fileName.split('.').pop();
      let fileType = 'unknown';
      if (checkFileType == '.txt') {
        fileType = 'text/plain';
      }
      if (checkFileType == '.pdf') {
        fileType = 'application/pdf';
      }
      if (checkFileType == '.doc') {
        fileType = 'application/vnd.ms-word';
      }
      if (checkFileType == '.docx') {
        fileType = 'application/vnd.ms-word';
      }
      if (checkFileType == '.xls') {
        fileType = 'application/vnd.ms-excel';
      }
      if (checkFileType == '.png') {
        fileType = 'image/png';
      }
      if (checkFileType == '.jpg') {
        fileType = 'image/jpeg';
      }
      if (checkFileType == '.jpeg') {
        fileType = 'image/jpeg';
      }
      if (checkFileType == '.gif') {
        fileType = 'image/gif';
      }
      if (checkFileType == '.csv') {
        fileType = 'text/csv';
      }

      this.groupEnrollmentService
        .DownloadFile(fileName, fileType)
        .subscribe((file) => {
          if (file.body) {
            const url = window.URL.createObjectURL(file.body);
            window.open(url);
          }
        });
    } catch {
      this.isLoading = false;
      this.alertService.error('Something went wrong. Please try again.');
    }
  }

  private setFormValue(groupEnrollment: IGroupEnrollment) {
    this.groupEnrollmentForm.patchValue({
      npi: groupEnrollment.npi.trim() || '',
      taxId: groupEnrollment.taxId.trim() || '',
      enrollmentStatus: groupEnrollment.enrollmentStatus || '',
      processorId: groupEnrollment.processorId || '',
      followUpDate: groupEnrollment.followUpDate ?? undefined,
      message: groupEnrollment.message || '',
      actionNotes: groupEnrollment.actionNotes || '',
      caseNumber: groupEnrollment.caseNumber || '',
    });

    const processorIdInput = this.groupEnrollmentForm.get('processorId');
    const caseNumber = this.groupEnrollmentForm.get('caseNumber');

    if (processorIdInput?.value) {
      processorIdInput.disable();
    }
    if (caseNumber?.value) {
      caseNumber.disable();
    }
  }

  private getAdditionalRequestsList() {
    const getGroupEnrollmentLogsRequest$ = this.groupEnrollmentGQLService
      .getGroupEnrollmentLogs({ seqNo: this.groupEnrollment.seqNo })
      .pipe(
        map((result) => {
          return result.nodes;
        }),
      );

    return [
      this.groupEnrollmentGQLService.getGroupEnrollmentStatuses(),
      this.groupEnrollmentService.getGroupEnrollmentPayers({
        ScId: this.groupEnrollment.payerId,
        EnrollmentType: this.groupEnrollment.enrollmentType,
      }),
      getGroupEnrollmentLogsRequest$,
    ];
  }

  close() {
    this.activeModal.dismiss();
  }

  onSave() {
    this.isLoading = true;

    const formValue = this.groupEnrollmentForm.getRawValue();
    const value: IGroupEnrollmentUpdate = {
      ...formValue,
      payerId: this.groupEnrollment.payerId.toString(),
      seqNo: this.groupEnrollment.seqNo,
    };

    this.groupEnrollmentService.editGroupEnrollment(value).subscribe({
      next: () => {
        this.isLoading = false;
        this.alertService.success(
          'Group Enrollment has been successfully modified!',
        );
        this.activeModal.close();
      },
      error: (error: unknown) => {
        this.isLoading = false;
        this.alertService.error(error as HttpErrorResponse | string);
      },
    });
  }

  editNote() {
    const modal = this.modal.open(EditGroupEnrollmentNoteModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'sm',
    });
    const modalInstance =
      modal.componentInstance as EditGroupEnrollmentNoteModalComponent;

    modalInstance.groupEnrollment = this.groupEnrollment;
  }

  private setPayer(payer: IGroupEnrollmentPayer) {
    this.selectedPayer = payer;
    this.documents = [
      this.selectedPayer.doc1,
      this.selectedPayer.doc2,
      this.selectedPayer.doc3,
      this.selectedPayer.doc4,
      this.selectedPayer.doc5,
    ].filter(Boolean);
    this.groupEnrollmentForm.patchValue({
      notes: this.selectedPayer.notes,
    });
  }
}
